import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Title } from 'react-admin';
import {
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    Container,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@material-ui/core';
import ApiNgUtil from '../api/ApiNgUtil';
import InvokeApiMethods from '../ExternalApi/ExternalApiModels';
import CreateRoundUtil from '../CreateRound/CreateRoundUtil';
import RoundOverviewUtil from '../RoundOverview/RoundOverviewUtil';
import { Fixture as FixtureNg, FixtureTimelineEvent } from '../TeamOverview/TeamOverviewModels';
import {
    THE_GAME_JACKPOT_KEY,
    THE_GAME_MANIFEST_KEY,
    TheGameManifest,
    TheGameRoundEntry,
    TheGameStandings as TheGameStandingsDto,
    TheGameStandingsMarket
} from '../CreateTheGameRound/TheGameModels';
import TheGameStandingsUtil, {
    EDITABLE_ROUND_STATUSES,
    FINISHED_STATUS,
    GOAL,
    MAX_NOTE_LENGTH,
    RED_CARD,
    SIGN_X,
    YELLOW_CARD,
    YELLOW_RED
} from './TheGameStandingsUtil';
import ManualEventForm from './ManualEventForm';
import FixtureDialog from '../RoundOverview/FixtureDialog';
import JsonExportDialog from './JsonExportDialog';
import JsonImportDialog from './JsonImportDialog';

interface TheGameStandingsParams {
    roundId: string;
}

type EventFilter = "GOALS" | "CARDS" | "YELLOW" | "RED" | "ALL";

const EVENT_FILTERS: { key: EventFilter, label: string, types: string[] | null, tooltip?: string }[] = [
    { key: "GOALS", label: "Goals", types: [GOAL] },
    {
        key: "CARDS", label: "Cards", types: [YELLOW_CARD, YELLOW_RED, RED_CARD],
        tooltip: "Card points, as in the Cards Over/Under market: yellow card = 1, second yellow (YELLOW_RED) = 2, red card = 2. FT (HT)."
    },
    {
        key: "YELLOW", label: "Yellow cards", types: [YELLOW_CARD],
        tooltip: "Number of yellow cards (YELLOW_CARD). A second yellow (YELLOW_RED) counts under Red cards. FT (HT)."
    },
    {
        key: "RED", label: "Red cards", types: [RED_CARD, YELLOW_RED],
        tooltip: "Number of red cards: direct red (RED_CARD) + second yellow (YELLOW_RED). FT (HT)."
    },
    { key: "ALL", label: "All", types: null }
];

const CHANGED_COLOR = "#fff3c4";

// One entry per manifest market, ordered by pos.
const toMarkets = (manifest: TheGameManifest, standings: TheGameStandingsDto | null): TheGameStandingsMarket[] => {
    return manifest.markets.slice().sort((a, b) => a.pos - b.pos).map(market => {
        var saved = standings?.markets?.find(it => it.pos === market.pos);
        return {
            pos: market.pos,
            sign: saved?.sign ?? SIGN_X,
            decided: saved?.decided ?? false,
            note: saved?.note ?? null
        };
    });
};

const toDateString = (value: string | Date | null | undefined): string => {
    return value ? CreateRoundUtil.toGameDateString(new Date(value)) : "";
};

const toFriendlyRow = (row: string[] | null | undefined): string => {
    return (row ?? []).map(sign => RoundOverviewUtil.getFriendlySign(sign) || "-").join(" ");
};

const TheGameStandings: React.FC = () => {
    const params: TheGameStandingsParams = useParams();
    const roundId = params.roundId;
    const history = useHistory();

    const [round, setRound] = useState<TheGameRoundEntry | null>(null);
    const [manifest, setManifest] = useState<TheGameManifest | null>(null);
    const [roundLoading, setRoundLoading] = useState<boolean>(false);
    const [roundError, setRoundError] = useState<string>("");

    const [baseline, setBaseline] = useState<TheGameStandingsMarket[]>([]);
    const [edits, setEdits] = useState<TheGameStandingsMarket[]>([]);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [warnings, setWarnings] = useState<Record<number, string>>({});

    const [source, setSource] = useState<FixtureNg | null>(null);
    const [sourceLoading, setSourceLoading] = useState<boolean>(false);
    const [sourceError, setSourceError] = useState<string>("");
    const [eventFilter, setEventFilter] = useState<EventFilter>("ALL");
    const [manualEvents, setManualEvents] = useState<FixtureTimelineEvent[]>([]);
    // eventKeys of excluded events; they stay listed but are left out of every calculation.
    const [excluded, setExcluded] = useState<string[]>([]);
    const [exportOpen, setExportOpen] = useState<boolean>(false);
    const [importOpen, setImportOpen] = useState<boolean>(false);

    const [saving, setSaving] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string>("");
    const [saveInfo, setSaveInfo] = useState<string>("");

    const loadRound = async (): Promise<TheGameRoundEntry | null> => {
        var resp = await ApiNgUtil.getObject<TheGameRoundEntry>(`the-game/rounds/${encodeURIComponent(roundId)}`);
        if (!resp.success) {
            setRoundError(`Get round failed: ${resp.message}`);
            return null;
        }
        if (!resp.item) {
            setRoundError(`Round ${roundId} not found`);
            return null;
        }
        var m = TheGameStandingsUtil.parseManifest(resp.item);
        setRound(resp.item);
        setManifest(m);
        if (!m) {
            setRoundError(`Round ${roundId} has no valid ${THE_GAME_MANIFEST_KEY} manifest`);
        }
        return resp.item;
    };

    const showStandings = (m: TheGameManifest, standings: TheGameStandingsDto | null) => {
        var markets = toMarkets(m, standings);
        setBaseline(markets);
        setEdits(markets);
        setUpdatedAt(standings?.updatedAt ?? null);
        setWarnings({});
    };

    // Saved standings come with the round; only a generated default needs the standings endpoint.
    const loadStandings = async (r: TheGameRoundEntry, m: TheGameManifest): Promise<void> => {
        var saved = TheGameStandingsUtil.parseStandings(r);
        if (saved) {
            showStandings(m, saved);
            return;
        }
        var resp = await ApiNgUtil.getObject<TheGameStandingsDto>(
            `the-game/rounds/${encodeURIComponent(roundId)}/standings?default=true`);
        if (!resp.success) {
            setRoundError(`Get standings failed: ${resp.message}`);
            return;
        }
        showStandings(m, resp.item);
    };

    const refreshRound = async (): Promise<TheGameManifest | null> => {
        setRoundLoading(true);
        setRoundError("");
        setSaveError("");
        setSaveInfo("");
        try {
            var r = await loadRound();
            var m = r ? TheGameStandingsUtil.parseManifest(r) : null;
            if (r && m) {
                await loadStandings(r, m);
            }
            return m;
        } catch (error) {
            setRoundError(CreateRoundUtil.getExceptionMessage(error, false, "Refresh round error"));
            return null;
        } finally {
            setRoundLoading(false);
        }
    };

    const refreshSource = async (fixtureId: number): Promise<void> => {
        setSourceLoading(true);
        setSourceError("");
        try {
            var resp = await ApiNgUtil.getObject<FixtureNg>(`fixtures/${fixtureId}?includeDetails=true`);
            if (!resp.success || !resp.item) {
                setSourceError(`Get source fixture ${fixtureId} failed: ${resp.message}`);
                return;
            }
            setSource(resp.item);
        } catch (error) {
            setSourceError(CreateRoundUtil.getExceptionMessage(error, false, "Refresh fixture error"));
        } finally {
            setSourceLoading(false);
        }
    };

    useEffect(() => {
        refreshRound().then(m => {
            if (m) {
                refreshSource(m.match.fixtureId);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roundId]);

    const editable = !!round && !!manifest && EDITABLE_ROUND_STATUSES.includes(round.status) && edits.length > 0;
    const busy = saving || roundLoading;

    // Manual (typed or imported) events are only used while the feed has sent none.
    const feedEmpty = !!source && (source.timelineEvents ?? []).length === 0;
    const eventSource = source && feedEmpty && manualEvents.length > 0
        ? TheGameStandingsUtil.withManualEvents(source, manualEvents) : source;
    const effectiveSource = eventSource ? TheGameStandingsUtil.withoutExcluded(eventSource, excluded) : null;

    const onImportEvents = (text: string): string | null => {
        try {
            var imported = TheGameStandingsUtil.parseImportedEvents(text);
            setManualEvents(imported.events);
            setExcluded(imported.excludedIds.map(id => `${id}#1`));
            return null;
        } catch (error) {
            return (error as Error).message;
        }
    };

    const isLocked = (pos: number): boolean => round?.fixtures?.[pos]?.status === FINISHED_STATUS;

    const hasChanges = edits.some(entry => {
        var base = baseline.find(it => it.pos === entry.pos);
        return !base || base.sign !== entry.sign || base.decided !== entry.decided
            || TheGameStandingsUtil.normalizeNote(base.note) !== TheGameStandingsUtil.normalizeNote(entry.note);
    });

    const updateEdit = (pos: number, change: Partial<TheGameStandingsMarket>) => {
        setEdits(edits.map(it => it.pos === pos ? { ...it, ...change } : it));
        setSaveInfo("");
    };

    const onApply = () => {
        if (!manifest || !effectiveSource || !round) {
            return;
        }
        var result = TheGameStandingsUtil.applySourceFixture(manifest, edits, effectiveSource, round.fixtures ?? []);
        setEdits(result.markets);
        setWarnings(result.warnings);
        setSaveError("");
        setSaveInfo("Applied from the source fixture. Not saved.");
    };

    const onUndo = () => {
        setEdits(baseline);
        setWarnings({});
        setSaveError("");
        setSaveInfo("");
    };

    const onSave = async () => {
        if (!manifest) {
            return;
        }
        setSaveError("");
        setSaveInfo("");
        var errors = edits.map(entry => {
            var market = manifest.markets.find(it => it.pos === entry.pos);
            var error = market ? TheGameStandingsUtil.checkNoteScore(market, entry) : null;
            return error ? `Market ${entry.pos}: ${error}` : null;
        }).filter(it => it !== null);
        if (errors.length > 0) {
            setSaveError(errors.join(". "));
            return;
        }
        var body: TheGameStandingsDto = {
            markets: edits.map(it => ({
                pos: it.pos,
                sign: it.sign,
                decided: it.decided,
                note: TheGameStandingsUtil.normalizeNote(it.note)
            }))
        };
        setSaving(true);
        try {
            var resp = await ApiNgUtil.getObjectWithBody<TheGameStandingsDto>(
                `the-game/rounds/${encodeURIComponent(roundId)}/standings`, InvokeApiMethods.PUT(), JSON.stringify(body));
            if (!resp.success || !resp.item) {
                setSaveError(`Save standings failed: ${resp.message}`);
                return;
            }
            showStandings(manifest, resp.item);
            setSaveInfo("Saved.");
            await loadRound();
        } catch (error) {
            setSaveError(CreateRoundUtil.getExceptionMessage(error, false, "Save standings error"));
        } finally {
            setSaving(false);
        }
    };

    const changedCell = (changed: boolean, was: string, content: React.ReactElement) => (
        <TableCell style={changed ? { backgroundColor: CHANGED_COLOR } : undefined}>
            <Tooltip title={changed ? `Was: ${was}` : ""}>
                <span>{content}</span>
            </Tooltip>
        </TableCell>
    );

    var roundBox = null;
    if (round) {
        var jackpot = (round.promotionValues ?? []).find(pv => pv.key === THE_GAME_JACKPOT_KEY)?.value;
        roundBox = (
            <Box fontSize={13}>
                <Box>{`Round: ${round.id} | Type: ${round.roundTypeName} | Status: ${round.status} | Result update mode: ${round.resultUpdateMode ?? ""} | OpenTo: ${toDateString(round.openTo)} | Jackpot EUR: ${jackpot ?? ""}`}</Box>
                <Box>{`Preliminary correct row: ${toFriendlyRow(round.preliminaryCorrectRow)} | Correct row: ${toFriendlyRow(round.correctRow)}`}</Box>
            </Box>
        );
    }

    var sourceBox = null;
    if (manifest) {
        var shown = effectiveSource;
        var manual = eventSource !== source;
        var events = TheGameStandingsUtil.toMatchOrder(eventSource?.timelineEvents);
        var eventKeys = TheGameStandingsUtil.eventKeys(events);
        var filterTypes = EVENT_FILTERS.find(it => it.key === eventFilter)?.types ?? null;
        var shownRows = events.map((e, i) => ({ event: e, key: eventKeys[i] }))
            .filter(row => !filterTypes || filterTypes.includes(row.event.type));
        var excludedCount = eventKeys.filter(key => excluded.includes(key)).length;
        var toggleExcluded = (key: string) =>
            setExcluded(excluded.includes(key) ? excluded.filter(it => it !== key) : [...excluded, key]);
        var players = shown ? TheGameStandingsUtil.getPlayerRows(shown) : [];
        var filterScores = shown ? TheGameStandingsUtil.getFilterScores(shown) : null;
        sourceBox = (
            <Box paddingTop={2}>
                <Box display="flex" alignItems="center">
                    <Typography variant="h6">Source fixture</Typography>&nbsp;&nbsp;
                    <Button variant="outlined" color="secondary" size="small" disabled={sourceLoading}
                            onClick={() => refreshSource(manifest.match.fixtureId)}>
                        {sourceLoading ? "Loading..." : "Refresh fixture"}
                    </Button>
                    {editable ? (
                        <>
                            &nbsp;
                            <Button variant="outlined" color="secondary" size="small" disabled={busy || sourceLoading || !source}
                                    onClick={() => onApply()}>
                                Apply
                            </Button>
                        </>
                    ) : null}
                </Box>
                {sourceError.length > 0 ? (<Box color={RoundOverviewUtil.INVALID_COLOR} fontSize={12}>{sourceError}</Box>) : null}
                <Box fontSize={13}>
                    {source ? (
                        <FixtureDialog open={false} fixture={source} nameLinkEnabled={true}
                                       onClose={saved => { if (saved) { refreshSource(manifest.match.fixtureId); } }} />
                    ) : `${manifest.match.home} - ${manifest.match.away}`}
                    {` | Kickoff: ${toDateString(source?.startingAt ?? manifest.match.kickoff)} | Fixture: ${manifest.match.fixtureId}`}
                    {source ? ` | Status: ${source.status} | Score: ${source.homeTeam?.score ?? 0}-${source.awayTeam?.score ?? 0}` : ""}
                    {shown ? ` | Event score: ${TheGameStandingsUtil.formatFtHt(TheGameStandingsUtil.getEventScore(shown))}` : ""}
                    {manual ? " (from manual/imported events)" : ""}
                </Box>
                {shown ? (
                    <Box display="flex" paddingTop={1} style={{ gap: 16 }} alignItems="flex-start">
                        <Box flex={3}>
                            <ButtonGroup size="small" color="secondary">
                                {EVENT_FILTERS.map(f => {
                                    var button = (
                                        <Button key={f.key} variant={eventFilter === f.key ? "contained" : "outlined"}
                                                onClick={() => setEventFilter(f.key)}>
                                            {f.label}
                                            {filterScores && f.key !== "ALL" ? ` ${TheGameStandingsUtil.formatFtHt(filterScores[f.key])}` : ""}
                                        </Button>
                                    );
                                    return f.tooltip ? (<Tooltip key={f.key} title={f.tooltip}>{button}</Tooltip>) : button;
                                })}
                            </ButtonGroup>
                            <TableContainer component={Paper} style={{ maxHeight: 400, marginTop: 8 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">Excl.</TableCell>
                                            <TableCell>Min</TableCell>
                                            <TableCell>Team</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Player</TableCell>
                                            <TableCell>Related player</TableCell>
                                            <TableCell>Result</TableCell>
                                            {manual ? (<TableCell />) : null}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {shownRows.map(({ event: e, key }) => (
                                            <TableRow key={key}
                                                      style={excluded.includes(key) ? { opacity: 0.45, textDecoration: "line-through" } : undefined}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox size="small" checked={excluded.includes(key)}
                                                              onChange={() => toggleExcluded(key)} />
                                                </TableCell>
                                                <TableCell>{TheGameStandingsUtil.formatMinute(e)}</TableCell>
                                                <TableCell>{TheGameStandingsUtil.teamName(e, shown!)}</TableCell>
                                                <TableCell>{e.type}</TableCell>
                                                <TableCell>{e.playerName ?? ""}</TableCell>
                                                <TableCell>{e.relatedPlayerName ?? ""}</TableCell>
                                                <TableCell>{e.result ?? ""}</TableCell>
                                                {manual ? (
                                                    <TableCell>
                                                        <Button size="small" color="secondary"
                                                                onClick={() => setManualEvents(manualEvents.filter(it => it.id !== e.id))}>
                                                            Remove
                                                        </Button>
                                                    </TableCell>
                                                ) : null}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box fontSize={11} display="flex" alignItems="center">
                                {`${shownRows.length} of ${events.length} events`}
                                {excludedCount > 0 ? (
                                    <>
                                        {` | ${excludedCount} excluded`}&nbsp;
                                        <Button size="small" color="secondary" onClick={() => setExcluded([])}>Include all</Button>
                                    </>
                                ) : null}
                                &nbsp;|&nbsp;
                                <Button size="small" color="secondary" onClick={() => setExportOpen(true)}>Export</Button>
                                {feedEmpty ? (
                                    <>
                                        &nbsp;|&nbsp;
                                        <Button size="small" color="secondary" onClick={() => setImportOpen(true)}>Import</Button>
                                    </>
                                ) : null}
                            </Box>
                            <JsonExportDialog
                                open={exportOpen}
                                title={`Source fixture events (${shownRows.length})`}
                                value={shownRows.map(row => excluded.includes(row.key) ? { ...row.event, excluded: true } : row.event)}
                                onClose={() => setExportOpen(false)}
                            />
                            <JsonImportDialog
                                open={importOpen}
                                title="Import source fixture events"
                                hint={"Paste an Export (array of events in match order) or a whole fixture JSON with timelineEvents. "
                                    + "The events replace the manual events in this page only; nothing is saved. \"excluded\": true marks an event as excluded."}
                                onImport={onImportEvents}
                                onClose={() => setImportOpen(false)}
                            />
                            {feedEmpty ? (
                                <ManualEventForm
                                    homeTeamName={source?.homeTeam?.name ?? manifest.match.home}
                                    awayTeamName={source?.awayTeam?.name ?? manifest.match.away}
                                    count={manualEvents.length}
                                    onAdd={event => setManualEvents([...manualEvents, event])}
                                    onClear={() => setManualEvents([])}
                                />
                            ) : null}
                        </Box>
                        <Box flex={2}>
                            <TableContainer component={Paper} style={{ maxHeight: 440 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Team</TableCell>
                                            <TableCell>Player</TableCell>
                                            <TableCell align="right">Goals</TableCell>
                                            <TableCell align="right">Yellow cards</TableCell>
                                            <TableCell align="right">Red cards</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {players.map(p => (
                                            <TableRow key={`${p.team}|${p.player}`}>
                                                <TableCell>{p.team}</TableCell>
                                                <TableCell>{p.player}</TableCell>
                                                <TableCell align="right">{p.goals}</TableCell>
                                                <TableCell align="right">{p.yellowCards}</TableCell>
                                                <TableCell align="right">{p.redCards}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>
                ) : null}
            </Box>
        );
    }

    var marketsBox = null;
    if (round && manifest && edits.length > 0) {
        var conflicts = effectiveSource ? TheGameStandingsUtil.getScoreConflicts(manifest, edits, effectiveSource) : {};
        marketsBox = (
            <Box paddingTop={2}>
                <Box display="flex" alignItems="center">
                    <Typography variant="h6">Standings</Typography>&nbsp;&nbsp;
                    <Box fontSize={12}>{updatedAt ? `Saved ${toDateString(updatedAt)}` : "Not saved (generated default)"}</Box>
                    {editable ? null : (<Box fontSize={12}>&nbsp;| Read-only: round is {round.status}</Box>)}
                </Box>
                <TableContainer component={Paper} style={{ marginTop: 8 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Pos</TableCell>
                                <TableCell>Market</TableCell>
                                <TableCell>Selections</TableCell>
                                <TableCell>Fixture</TableCell>
                                <TableCell>Result</TableCell>
                                <TableCell>Sign</TableCell>
                                <TableCell>Decided</TableCell>
                                <TableCell>Note</TableCell>
                                <TableCell>Warning</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {edits.map(entry => {
                                var market = manifest.markets.find(it => it.pos === entry.pos)!;
                                var base = baseline.find(it => it.pos === entry.pos);
                                var fixture = round.fixtures?.[entry.pos];
                                var result = round.fixtureResults?.[entry.pos];
                                var locked = isLocked(entry.pos);
                                var disabled = !editable || busy;
                                var baseNote = TheGameStandingsUtil.normalizeNote(base?.note);
                                var noteError = TheGameStandingsUtil.checkNoteScore(market, entry);
                                return (
                                    <TableRow key={entry.pos}>
                                        <TableCell>{entry.pos}</TableCell>
                                        <TableCell>{market.label}</TableCell>
                                        <TableCell>
                                            {TheGameStandingsUtil.selectionKeys(market).map(key => (
                                                <Box key={key} fontSize={12}>{`${key}: ${market.selections[key]}`}</Box>
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            {fixture ? (
                                                <Box fontSize={12}>
                                                    <Box>{fixture.id}</Box>
                                                    <Box>{`${fixture.status} ${fixture.homeTeam?.score ?? 0}-${fixture.awayTeam?.score ?? 0}`}</Box>
                                                </Box>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            {result ? `${RoundOverviewUtil.getFriendlySign(result.sign) || "-"} ${result.status}` : ""}
                                        </TableCell>
                                        {changedCell(!!base && base.sign !== entry.sign, base?.sign ?? "", (
                                            <TextField
                                                select
                                                value={entry.sign}
                                                disabled={disabled || locked}
                                                onChange={e => updateEdit(entry.pos, { sign: e.target.value })}
                                                style={{ minWidth: 140 }}
                                            >
                                                {TheGameStandingsUtil.selectionKeys(market).map(key => (
                                                    <MenuItem key={key} value={key}>{`${key}: ${market.selections[key]}`}</MenuItem>
                                                ))}
                                            </TextField>
                                        ))}
                                        {changedCell(!!base && base.decided !== entry.decided, base?.decided ? "decided" : "not decided", (
                                            <Checkbox
                                                checked={entry.decided}
                                                disabled={disabled || locked}
                                                onChange={e => updateEdit(entry.pos, { decided: e.target.checked })}
                                            />
                                        ))}
                                        {changedCell(!!base && baseNote !== TheGameStandingsUtil.normalizeNote(entry.note), baseNote ?? "(empty)", (
                                            <TextField
                                                value={entry.note ?? ""}
                                                disabled={disabled}
                                                inputProps={{ maxLength: MAX_NOTE_LENGTH }}
                                                error={!!noteError}
                                                helperText={noteError}
                                                onChange={e => updateEdit(entry.pos, { note: e.target.value })}
                                                style={{ minWidth: 180 }}
                                            />
                                        ))}
                                        <TableCell style={{ color: RoundOverviewUtil.INVALID_COLOR, fontSize: 12 }}>
                                            {locked ? "Locked (fixture FINISHED). " : ""}{warnings[entry.pos] ?? ""}
                                            {conflicts[entry.pos] ? (<Box>{conflicts[entry.pos]}</Box>) : null}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {editable ? (
                    <Box paddingTop={2}>
                        <Button variant="outlined" color="secondary" disabled={busy || (!hasChanges && Object.keys(warnings).length === 0)} onClick={() => onUndo()}>
                            Undo
                        </Button>&nbsp;
                        <Button variant="contained" color="primary" disabled={busy || (!hasChanges && !!updatedAt)} onClick={() => onSave()}>
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Box>
                ) : null}
                {saveError.length > 0 ? (<Box color={RoundOverviewUtil.INVALID_COLOR} fontSize={12}>{saveError}</Box>) : null}
                {saveInfo.length > 0 ? (<Box color="blue" fontSize={12}>{saveInfo}</Box>) : null}
            </Box>
        );
    }

    return (
        <Container maxWidth="xl">
            <Title title="The Game Standings" />
            <Box paddingBottom={1}>
                <Button variant="outlined" color="secondary" size="small" onClick={() => history.push('/roundOverview')}>
                    Round Overview
                </Button>&nbsp;
                <Button variant="contained" color="secondary" size="small" disabled={busy} onClick={() => refreshRound()}>
                    {roundLoading ? "Loading..." : "Refresh round"}
                </Button>
            </Box>
            {roundError.length > 0 ? (<Box color={RoundOverviewUtil.INVALID_COLOR} fontSize={12}>{roundError}</Box>) : null}
            {roundBox}
            {sourceBox}
            {marketsBox}
        </Container>
    );
};

export default TheGameStandings;
